/**
 * MobileMicPage — página /mic/:token aberta no celular do médico
 * (web /ia, Fase Voice S3 — sprint 20/05/2026, refactor 23/05/2026).
 *
 * Fluxo geral:
 *   1. Médico configura voice no PC (S1+S2) → gera QR code.
 *   2. Médico escaneia o QR no celular → abre `/mic/:token?lang=xx`.
 *   3. Página valida token via `GET /api/voice/token-info/:token` (backend
 *      marca state='paired' e emite evento SSE 'pairing.connected' pro PC).
 *   4. Pede permissão de microfone.
 *   5. Mostra botão GRANDE coral "GRAVAR CONSULTA".
 *   6. MediaRecorder grava áudio (Opus webm; fallback mp4 pra iOS Safari).
 *   7. Upload via `POST /api/voice/transcribe` (FormData com pairing_token).
 *   8. Mostra "✅ ENVIADO" 1.5s e volta pro botão (queue: gravar várias).
 *   9. Wake Lock mantém tela acordada (re-aquisição em visibilitychange).
 *
 * Refactor 23/05/2026 — 13 melhorias integradas (sub-agent paralelo):
 *   #1  Idioma: ?lang URL > navigator.language > localStorage > pt-BR
 *   #2  VoiceWave coral animado durante recording
 *   #3  LedPanel KR no header (mapeia micState → variant)
 *   #5  BatteryIndicator (auto-detect Battery API, <20% mostra)
 *   #6  Countdown 12h sessão (refresh 1x/min)
 *   #8  Pause/Resume mid-recording (novo state 'paused')
 *   #9  Switcher idioma 5 botões discreto no header
 *   #12 WakeLockBadge no rodapé (active/failed)
 *   #13 Botão emergência "encerrar sessão" (cleanup local; backend não tem
 *       endpoint público — TODO Fase Voice S4)
 *
 * iOS Safari: Wake Lock chegou na 16.4 (mar/2023). Em versões anteriores
 * cai num hint pequeno pedindo pro médico travar a tela manualmente nas
 * configs. MediaRecorder.audio/webm não é suportado no Safari iOS → usa
 * audio/mp4 como fallback (Whisper aceita ambos).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FC,
} from 'react';
import type { Locale } from '../types';
import { LedPanel, type LedVariant } from './mobile/LedPanel';
import { VoiceWave } from './mobile/VoiceWave';
import { BatteryIndicator } from './mobile/BatteryIndicator';
import { WakeLockBadge } from './mobile/WakeLockBadge';

/* ------------------------------------------------------------------------- */
/* Tipos públicos                                                              */
/* ------------------------------------------------------------------------- */

/** Estados da máquina principal da página. */
type MicState =
  | 'validating'      // GET /token-info em curso
  | 'invalid_token'   // 404 / token expirado / sessão encerrada manualmente
  | 'requesting_mic'  // pedindo permissão do navegador
  | 'mic_denied'      // user negou ou erro de getUserMedia
  | 'ready'           // pronto pra gravar (botão grande)
  | 'recording'       // MediaRecorder ativo
  | 'paused'          // MediaRecorder em pausa (mid-recording) — NOVO #8
  | 'uploading'       // POST /transcribe em curso
  | 'uploaded'        // sucesso, mostrando "✅ ENVIADO" (1.5s)
  | 'error';          // erro de upload/Whisper/etc (volta 'ready' em 3s)

/** Tier retornado pelo backend (informativo, exibido como badge no rodapé). */
type TokenTier = 'especialista' | 'unlimited';

/** Item da fila de gravações enviadas (mostra histórico curto na tela). */
interface QueuedRecording {
  readonly id: string;
  readonly seconds: number;
  readonly sentAt: number;
}

export interface MobileMicPageProps {
  /** Token de pareamento extraído da URL (`/mic/:token`). */
  readonly token: string;
}

/* ------------------------------------------------------------------------- */
/* Endpoints                                                                   */
/* ------------------------------------------------------------------------- */

/**
 * URL base do worker — mesmo padrão de api/client.ts.
 * Vite injeta `VITE_API_BASE` em build via .env.
 */
const VOICE_API_BASE: string =
  import.meta.env.VITE_API_BASE ?? 'https://drpera-api.luperazolo.workers.dev';

/** Constrói URL do endpoint token-info. */
function buildTokenInfoUrl(token: string): string {
  return `${VOICE_API_BASE}/api/voice/token-info/${encodeURIComponent(token)}`;
}

const TRANSCRIBE_ENDPOINT = `${VOICE_API_BASE}/api/voice/transcribe`;

/* ------------------------------------------------------------------------- */
/* i18n minimal — espelha o padrão de useTranslations.ts (sem dependência)    */
/* ------------------------------------------------------------------------- */

/** Chave do localStorage com idioma persistido (mesma do useTranslations). */
const LOCALE_KEY = 'drpera-locale';
const DEFAULT_LOCALE: Locale = 'pt-BR';

/** Lista de locales suportados — usado pelo switcher e pela validação. */
const SUPPORTED_LOCALES: ReadonlyArray<Locale> = ['pt-BR', 'es', 'it', 'fr', 'en'];

/** Type-guard pro Locale (usado em URL param + storage validation). */
function isValidLocale(v: unknown): v is Locale {
  return (
    typeof v === 'string' &&
    (v === 'pt-BR' || v === 'es' || v === 'it' || v === 'fr' || v === 'en')
  );
}

type TranslationValue = string | Record<string, TranslationValue>;
type TranslationMap = Record<string, TranslationValue>;

/** Resolve chave de ponto no JSON de tradução (fallback: a própria chave). */
function resolvePath(map: TranslationMap, path: string): string {
  // 1) Tenta chave plana (compat com merges de staging)
  const flat = map[path];
  if (typeof flat === 'string') return flat;

  // 2) Tenta caminho aninhado
  const parts = path.split('.');
  let current: TranslationValue = map;
  for (const part of parts) {
    if (typeof current !== 'object' || current === null || !(part in current)) {
      return path;
    }
    current = (current as Record<string, TranslationValue>)[part];
  }
  return typeof current === 'string' ? current : path;
}

/**
 * Lê locale com PRIORIDADE (refactor #1 — 23/05/2026):
 *   1. URL param ?lang=xx — PC injeta no QR (fonte mais confiável; médico
 *      escaneou no celular que pode ter idioma diferente do PC).
 *   2. navigator.language — preferência do dispositivo móvel.
 *   3. localStorage — visitas anteriores ao ia.drpera.com.br.
 *   4. Fallback pt-BR.
 */
function readStoredLocale(): Locale {
  // 1) URL param (?lang=xx)
  try {
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    if (isValidLocale(urlLang)) return urlLang;
  } catch {
    /* SSR / sandbox sem window — ignora */
  }

  // 2) navigator.language do celular
  try {
    const nav = navigator.language || '';
    if (nav.startsWith('pt')) return 'pt-BR';
    if (nav.startsWith('es')) return 'es';
    if (nav.startsWith('it')) return 'it';
    if (nav.startsWith('fr')) return 'fr';
    if (nav.startsWith('en')) return 'en';
  } catch {
    /* ignore */
  }

  // 3) localStorage (sticky entre visitas)
  try {
    const stored = localStorage.getItem(LOCALE_KEY);
    if (isValidLocale(stored)) return stored;
  } catch {
    /* ignore */
  }

  // 4) Fallback
  return DEFAULT_LOCALE;
}

/** Persiste locale no localStorage (idem useTranslations). */
function persistLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_KEY, locale);
  } catch {
    /* ignore — modo privado, quota cheia, etc */
  }
}

/** Carrega JSON de tradução do locale (import dinâmico — chunk separado). */
async function loadTranslations(locale: Locale): Promise<TranslationMap> {
  switch (locale) {
    case 'es':
      return (await import('../i18n/es.json')).default as TranslationMap;
    case 'it':
      return (await import('../i18n/it.json')).default as TranslationMap;
    case 'fr':
      return (await import('../i18n/fr.json')).default as TranslationMap;
    case 'en':
      return (await import('../i18n/en.json')).default as TranslationMap;
    default:
      return (await import('../i18n/pt-BR.json')).default as TranslationMap;
  }
}

/* ------------------------------------------------------------------------- */
/* Helpers puros                                                               */
/* ------------------------------------------------------------------------- */

/**
 * Escolhe o melhor MIME type suportado pelo navegador.
 * - Chrome/Firefox Android: opus webm (qualidade alta, tamanho pequeno).
 * - iOS Safari 14.5+: audio/mp4 (Whisper aceita).
 * - String vazia = default do browser (último recurso).
 */
function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
  if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
  if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4';
  return '';
}

/** Extensão de arquivo derivada do MIME — só pra Whisper detectar formato. */
function fileExtensionForMime(mime: string): string {
  if (mime.includes('webm')) return 'webm';
  if (mime.includes('mp4')) return 'mp4';
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('wav')) return 'wav';
  return 'webm';
}

/** Formata segundos como "MM:SS". */
function formatTimer(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Formata duração restante em formato compacto (refactor #6 — countdown).
 *   - >=1h  → "11h 47m"
 *   - <1h   → "47m 12s"
 *   - <=0   → "00m 00s" (expirado — caller decide o que mostrar)
 */
function formatRemaining(ms: number): string {
  if (ms <= 0) return '00m 00s';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h >= 1) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
}

/**
 * Mapeia o `MicState` da máquina pra `LedVariant` do `<LedPanel>`
 * (refactor #3 — feedback visual KR no header).
 *
 *   validating / requesting_mic → amber  (conectando)
 *   ready                        → green  (conectado estável)
 *   recording                    → green-rec (rec rápido)
 *   paused                       → amber  (pausado — chama atenção)
 *   uploading                    → green-rec (atividade rede)
 *   uploaded                     → green  (volta estável)
 *   error / mic_denied / invalid_token → red
 *   default                      → off
 */
function mapMicStateToLed(s: MicState): LedVariant {
  switch (s) {
    case 'validating':
    case 'requesting_mic':
    case 'paused':
      return 'amber';
    case 'ready':
    case 'uploaded':
      return 'green';
    case 'recording':
    case 'uploading':
      return 'green-rec';
    case 'error':
    case 'mic_denied':
    case 'invalid_token':
      return 'red';
    default:
      return 'off';
  }
}

/**
 * Adquire Wake Lock pra manter tela do celular ligada durante a sessão.
 * Falha graciosamente em browsers/devices sem suporte (iOS <16.4, Firefox iOS, etc).
 */
type WakeLockHandle = { release: () => Promise<void> };

interface WakeLockAPI {
  readonly request: (type: 'screen') => Promise<WakeLockHandle>;
}

async function acquireWakeLock(): Promise<WakeLockHandle | null> {
  try {
    const nav = navigator as Navigator & { wakeLock?: WakeLockAPI };
    if (typeof nav.wakeLock?.request === 'function') {
      return await nav.wakeLock.request('screen');
    }
  } catch {
    // Negado pelo user, sem permissão, ou unsupported — sem problema.
  }
  return null;
}

/** Detecta iOS Safari (pra mostrar hint específico quando Wake Lock falha). */
function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  return /iP(hone|od|ad)/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua);
}

/* ------------------------------------------------------------------------- */
/* Tipos da resposta do backend                                                */
/* ------------------------------------------------------------------------- */

interface TokenInfoOk {
  readonly ok: true;
  readonly valid: true;
  readonly tier: TokenTier;
  readonly state: string;
  readonly expiresAt: number;
}

interface TranscribeOk {
  readonly ok: true;
  readonly audio_seconds: number;
}

interface BackendError {
  readonly ok: false;
  readonly reason: string;
  readonly message?: string;
}

/* ------------------------------------------------------------------------- */
/* Componente                                                                  */
/* ------------------------------------------------------------------------- */

const MobileMicPage: FC<MobileMicPageProps> = ({ token }) => {
  /* ── State principal ── */
  const [micState, setMicState] = useState<MicState>('validating');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tier, setTier] = useState<TokenTier | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [queue, setQueue] = useState<ReadonlyArray<QueuedRecording>>([]);
  const [wakeLockFailed, setWakeLockFailed] = useState(false);
  /**
   * Mirror do `wakeLockRef.current !== null` em state — necessário pra
   * `<WakeLockBadge>` re-renderizar (refs não disparam render). #12
   */
  const [wakeLockActive, setWakeLockActive] = useState(false);
  /**
   * Tick monotônico pra forçar re-render do countdown #6 a cada minuto.
   * Não usamos `Date.now()` em state pra evitar invalidar deps de hooks.
   */
  const [, setCountdownTick] = useState(0);

  /* ── i18n (refactor #9 — setter exposto pro switcher) ── */
  const [locale, setLocaleState] = useState<Locale>(() => readStoredLocale());
  const [translations, setTranslations] = useState<TranslationMap>({});
  const t = useCallback(
    (key: string, vars?: Record<string, string | number>): string => {
      let text = resolvePath(translations, key);
      if (vars) {
        Object.entries(vars).forEach(([k, v]) => {
          text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
        });
      }
      return text;
    },
    [translations],
  );

  /** Troca idioma + persiste (refactor #9). */
  const setLocale = useCallback((newLocale: Locale): void => {
    setLocaleState(newLocale);
    persistLocale(newLocale);
  }, []);

  useEffect(() => {
    let alive = true;
    void loadTranslations(locale).then((data) => {
      if (alive) setTranslations(data);
    });
    return () => {
      alive = false;
    };
  }, [locale]);

  /* ── Refs persistentes (não causam render) ── */
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeTypeRef = useRef<string>('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wakeLockRef = useRef<WakeLockHandle | null>(null);
  const uploadAbortRef = useRef<AbortController | null>(null);
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Locale-aware: mostra "iOS hint" só em Safari iOS sem Wake Lock. */
  const showIosWakeHint = useMemo(() => wakeLockFailed && isIosSafari(), [wakeLockFailed]);

  /* ──────────────────────────────────────────────────────────────────────
     Inicialização: valida token → pede mic → Wake Lock → 'ready'
     ────────────────────────────────────────────────────────────────────── */

  useEffect(() => {
    let cancelled = false;

    async function bootstrap(): Promise<void> {
      // ── 1) Valida token via GET /api/voice/token-info/:token ──
      try {
        const res = await fetch(buildTokenInfoUrl(token), { method: 'GET' });
        if (cancelled) return;

        if (!res.ok) {
          setMicState('invalid_token');
          return;
        }
        const body = (await res.json()) as TokenInfoOk | BackendError;
        if (cancelled) return;

        if ('ok' in body && body.ok === true && body.valid === true) {
          setTier(body.tier);
          setExpiresAt(typeof body.expiresAt === 'number' ? body.expiresAt : null);
        } else {
          setMicState('invalid_token');
          return;
        }
      } catch {
        if (cancelled) return;
        setMicState('invalid_token');
        return;
      }

      // ── 2) Pede permissão de microfone ──
      if (cancelled) return;
      setMicState('requesting_mic');

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        if (cancelled) return;
        setMicState('mic_denied');
        return;
      }
      if (cancelled) {
        // Race: usuário fechou antes da permissão — solta o stream
        stream.getTracks().forEach((tr) => {
          tr.stop();
        });
        return;
      }
      streamRef.current = stream;
      mimeTypeRef.current = pickMimeType();

      // ── 3) Tenta Wake Lock (graceful fallback) ──
      const lock = await acquireWakeLock();
      if (cancelled) {
        if (lock) {
          void lock.release();
        }
        return;
      }
      wakeLockRef.current = lock;
      if (lock === null) {
        setWakeLockFailed(true);
        setWakeLockActive(false);
      } else {
        setWakeLockActive(true);
      }

      // ── 4) Pronto pra gravar ──
      setMicState('ready');
    }

    void bootstrap();

    return () => {
      cancelled = true;
    };
    // token nunca muda durante o lifecycle desta página (vem da URL)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ──────────────────────────────────────────────────────────────────────
     Re-aquisição de Wake Lock quando a aba volta visível (Page Visibility)
     Cobre caso: médico atende WhatsApp e volta pra página.
     ────────────────────────────────────────────────────────────────────── */

  useEffect(() => {
    const handler = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (wakeLockRef.current !== null) return;
      void acquireWakeLock().then((lock) => {
        if (lock !== null) {
          wakeLockRef.current = lock;
          setWakeLockFailed(false);
          setWakeLockActive(true);
        }
      });
    };
    document.addEventListener('visibilitychange', handler);
    return () => {
      document.removeEventListener('visibilitychange', handler);
    };
  }, []);

  /* ──────────────────────────────────────────────────────────────────────
     Countdown sessão 12h (#6) — tick a cada 60s. Não precisa precisão
     menor; refresh mais rápido só queima bateria sem mudar a UI.
     ────────────────────────────────────────────────────────────────────── */

  useEffect(() => {
    if (expiresAt === null) return;
    const id = setInterval(() => {
      setCountdownTick((n) => n + 1);
    }, 60 * 1000);
    return () => {
      clearInterval(id);
    };
  }, [expiresAt]);

  /* ──────────────────────────────────────────────────────────────────────
     Cleanup geral no unmount: solta mic, Wake Lock, aborta upload, timers.
     ────────────────────────────────────────────────────────────────────── */

  useEffect(() => {
    return () => {
      if (recorderRef.current !== null && recorderRef.current.state !== 'inactive') {
        try {
          recorderRef.current.stop();
        } catch {
          /* ignore */
        }
      }
      if (streamRef.current !== null) {
        streamRef.current.getTracks().forEach((tr) => {
          tr.stop();
        });
        streamRef.current = null;
      }
      if (wakeLockRef.current !== null) {
        void wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
      if (uploadAbortRef.current !== null) {
        uploadAbortRef.current.abort();
        uploadAbortRef.current = null;
      }
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (feedbackTimerRef.current !== null) {
        clearTimeout(feedbackTimerRef.current);
        feedbackTimerRef.current = null;
      }
    };
  }, []);

  /* ──────────────────────────────────────────────────────────────────────
     Upload da gravação → POST /api/voice/transcribe
     Retorna boolean: true=sucesso, false=erro (state vira 'error').
     ────────────────────────────────────────────────────────────────────── */

  const uploadRecording = useCallback(
    async (blob: Blob): Promise<void> => {
      setMicState('uploading');

      const controller = new AbortController();
      uploadAbortRef.current = controller;

      try {
        const formData = new FormData();
        formData.append('pairing_token', token);
        const ext = fileExtensionForMime(blob.type || mimeTypeRef.current);
        formData.append('audio', blob, `audio.${ext}`);

        const res = await fetch(TRANSCRIBE_ENDPOINT, {
          method: 'POST',
          body: formData,
          signal: controller.signal,
        });
        uploadAbortRef.current = null;

        if (!res.ok) {
          // Tenta extrair mensagem do backend; fallback genérico.
          let msg = `HTTP ${res.status}`;
          try {
            const body = (await res.json()) as BackendError;
            if (typeof body.message === 'string') msg = body.message;
          } catch {
            /* sem JSON */
          }
          setErrorMessage(msg);
          setMicState('error');
          // Volta automaticamente pro estado 'ready' em 3s pra permitir nova tentativa
          feedbackTimerRef.current = setTimeout(() => {
            setErrorMessage(null);
            setMicState('ready');
          }, 3000);
          return;
        }

        const body = (await res.json()) as TranscribeOk;
        // Adiciona à fila local (histórico curto na tela)
        setQueue((prev) => [
          {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            seconds: typeof body.audio_seconds === 'number' ? body.audio_seconds : 0,
            sentAt: Date.now(),
          },
          ...prev,
        ].slice(0, 5));

        setMicState('uploaded');
        // Volta pro botão GRANDE depois de 1.5s — fluxo de queue.
        feedbackTimerRef.current = setTimeout(() => {
          setMicState('ready');
        }, 1500);
      } catch (err) {
        uploadAbortRef.current = null;
        if (err instanceof Error && err.name === 'AbortError') return;
        setErrorMessage(t('mic.error_network'));
        setMicState('error');
        feedbackTimerRef.current = setTimeout(() => {
          setErrorMessage(null);
          setMicState('ready');
        }, 3000);
      }
    },
    [token, t],
  );

  /* ──────────────────────────────────────────────────────────────────────
     Start/stop/pause/resume da gravação.
     ────────────────────────────────────────────────────────────────────── */

  const startRecording = useCallback((): void => {
    if (streamRef.current === null) return;
    if (micState !== 'ready') return;

    chunksRef.current = [];
    const mime = mimeTypeRef.current;
    const options: MediaRecorderOptions = mime !== '' ? { mimeType: mime } : {};

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(streamRef.current, options);
    } catch {
      // Browser não aceita o mime escolhido — tenta sem options.
      try {
        recorder = new MediaRecorder(streamRef.current);
      } catch {
        setErrorMessage(t('mic.error_recorder_init'));
        setMicState('error');
        return;
      }
    }

    recorder.ondataavailable = (ev): void => {
      if (ev.data.size > 0) chunksRef.current.push(ev.data);
    };

    recorder.onstop = (): void => {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      const finalMime = chunksRef.current[0]?.type || mime || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: finalMime });
      chunksRef.current = [];
      // Reset timer pra próxima gravação
      setRecordingSeconds(0);
      // Dispara upload (state vai pra 'uploading' lá dentro)
      void uploadRecording(blob);
    };

    recorder.onerror = (): void => {
      setErrorMessage(t('mic.error_recorder'));
      setMicState('error');
      feedbackTimerRef.current = setTimeout(() => {
        setErrorMessage(null);
        setMicState('ready');
      }, 3000);
    };

    recorderRef.current = recorder;
    recorder.start();

    setRecordingSeconds(0);
    timerRef.current = setInterval(() => {
      setRecordingSeconds((prev) => prev + 1);
    }, 1000);
    setMicState('recording');
  }, [micState, uploadRecording, t]);

  const stopRecording = useCallback((): void => {
    if (recorderRef.current === null) return;
    if (recorderRef.current.state === 'inactive') return;
    try {
      recorderRef.current.stop(); // dispara onstop → uploadRecording
    } catch {
      /* ignore */
    }
  }, []);

  /**
   * Pausa a gravação mid-stream (#8) — MediaRecorder mantém os chunks já
   * capturados e SUSPENDE captura nova até `.resume()`. Timer da UI também
   * para pra refletir o tempo real gravado.
   */
  const pauseRecording = useCallback((): void => {
    if (recorderRef.current === null) return;
    if (recorderRef.current.state !== 'recording') return;
    try {
      recorderRef.current.pause();
    } catch {
      return;
    }
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setMicState('paused');
  }, []);

  /** Retoma gravação pausada (#8) — re-arma timer e volta pro state. */
  const resumeRecording = useCallback((): void => {
    if (recorderRef.current === null) return;
    if (recorderRef.current.state !== 'paused') return;
    try {
      recorderRef.current.resume();
    } catch {
      return;
    }
    timerRef.current = setInterval(() => {
      setRecordingSeconds((prev) => prev + 1);
    }, 1000);
    setMicState('recording');
  }, []);

  /* ──────────────────────────────────────────────────────────────────────
     Retry quando mic foi negado: tenta de novo getUserMedia.
     ────────────────────────────────────────────────────────────────────── */

  const retryMicPermission = useCallback(async (): Promise<void> => {
    setMicState('requesting_mic');
    setErrorMessage(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      mimeTypeRef.current = pickMimeType();
      setMicState('ready');
    } catch {
      setMicState('mic_denied');
    }
  }, []);

  /* ──────────────────────────────────────────────────────────────────────
     Encerramento manual da sessão (#13) — pânico/fim de plantão.
     Cleanup PURAMENTE LOCAL — backend não tem endpoint público de
     cancel-session ainda (TODO Fase Voice S4 — `expireToken` existe
     internamente mas não é exposto). PC vai detectar via SSE timeout.
     ────────────────────────────────────────────────────────────────────── */

  const endSession = useCallback((): void => {
    const confirmMsg = t('mobile.end_session.confirm', {
      defaultValue: 'Tem certeza? Vai desconectar o PC também.',
    });
    // Confirm é OK em mobile — bloqueia até resposta. Único caso onde
    // perder o tap por mistake é pior do que mostrar dialog nativo.
    if (typeof window !== 'undefined' && !window.confirm(confirmMsg)) return;

    // 1) Para MediaRecorder se ativo (não dispara upload — descarta chunks)
    if (recorderRef.current !== null) {
      try {
        recorderRef.current.ondataavailable = null;
        recorderRef.current.onstop = null;
        recorderRef.current.onerror = null;
        if (recorderRef.current.state !== 'inactive') {
          recorderRef.current.stop();
        }
      } catch {
        /* ignore */
      }
      recorderRef.current = null;
    }
    chunksRef.current = [];

    // 2) Solta stream do microfone
    if (streamRef.current !== null) {
      streamRef.current.getTracks().forEach((tr) => {
        tr.stop();
      });
      streamRef.current = null;
    }

    // 3) Libera Wake Lock
    if (wakeLockRef.current !== null) {
      void wakeLockRef.current.release();
      wakeLockRef.current = null;
      setWakeLockActive(false);
    }

    // 4) Aborta upload em andamento
    if (uploadAbortRef.current !== null) {
      uploadAbortRef.current.abort();
      uploadAbortRef.current = null;
    }

    // 5) Para timers
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (feedbackTimerRef.current !== null) {
      clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = null;
    }

    // 6) UI vai pra tela final — reusa 'invalid_token' (mesma estética).
    setMicState('invalid_token');
    setErrorMessage(null);
    setRecordingSeconds(0);
  }, [t]);

  /* ──────────────────────────────────────────────────────────────────────
     Derived: countdown da sessão em ms (recomputado a cada tick #6).
     ────────────────────────────────────────────────────────────────────── */
  const sessionRemainingMs = expiresAt !== null ? expiresAt - Date.now() : null;
  const ledVariant = mapMicStateToLed(micState);

  /* ──────────────────────────────────────────────────────────────────────
     Render — telas por estado.
     ────────────────────────────────────────────────────────────────────── */

  return (
    <div style={pageStyle}>
      {/* Banner topo */}
      <header style={headerStyle}>
        {/* Bateria flutua absoluta no canto direito do header (#5) */}
        <BatteryIndicator t={t} />

        <div style={bannerStyle}>DR. PERA · VOICE</div>

        {/* Linha de status: LedPanel KR (#3) + tier badge */}
        <div style={statusRowStyle}>
          <LedPanel variant={ledVariant} />
          {tier !== null && (
            <span style={tierBadgeStyle}>
              {tier === 'unlimited' ? 'UNLIMITED' : 'ESPECIALISTA'}
            </span>
          )}
        </div>

        {/* Switcher de idioma (#9) — 5 botões discretos */}
        <div style={langSwitcherStyle} role="group" aria-label={t('mobile.lang.switcher_label', { defaultValue: 'Idioma' })}>
          {SUPPORTED_LOCALES.map((loc) => {
            const isActive = loc === locale;
            const labelMap: Record<Locale, string> = {
              'pt-BR': 'PT',
              es: 'ES',
              it: 'IT',
              fr: 'FR',
              en: 'EN',
            };
            return (
              <button
                key={loc}
                type="button"
                onClick={() => {
                  setLocale(loc);
                }}
                aria-pressed={isActive}
                style={{
                  ...langButtonStyle,
                  color: isActive ? '#FFFFFF' : 'var(--ink-mute)',
                  borderColor: isActive ? 'var(--violet)' : 'transparent',
                  textShadow: 'none',
                }}
              >
                {labelMap[loc]}
              </button>
            );
          })}
        </div>
      </header>

      {/* Corpo central — muda por state */}
      <main style={mainStyle}>
        {micState === 'validating' && (
          <StatusBlock
            text={t('mic.validating')}
            spinner
          />
        )}

        {micState === 'invalid_token' && (
          <StatusBlock
            text={t('mic.invalid_token')}
            tone="coral"
          />
        )}

        {micState === 'requesting_mic' && (
          <StatusBlock
            text={t('mic.requesting_mic')}
            spinner
          />
        )}

        {micState === 'mic_denied' && (
          <div style={blockStyle}>
            <h2 style={titleCoralStyle}>{t('mic.mic_denied_title')}</h2>
            <p style={hintStyle}>{t('mic.mic_denied_body')}</p>
            <button
              type="button"
              onClick={() => {
                void retryMicPermission();
              }}
              style={smallButtonStyle}
            >
              {t('mic.mic_denied_retry')}
            </button>
          </div>
        )}

        {micState === 'ready' && (
          <BigButton
            variant="record"
            label={t('mic.ready_button')}
            hint={t('mic.ready_hint')}
            onClick={startRecording}
          />
        )}

        {micState === 'recording' && (
          <div style={blockStyle}>
            <BigButton
              variant="stop"
              label={t('mic.recording_button')}
              hint={t('mic.recording_hint')}
              onClick={stopRecording}
              timer={formatTimer(recordingSeconds)}
            />
            {/* Wave verde animada durante recording (#2) — live state permitido */}
            <VoiceWave active={true} color="var(--green-fluor)" />
            {/* Botões pause/stop secundários (#8) */}
            <div style={secondaryButtonRowStyle}>
              <button type="button" onClick={pauseRecording} style={secondaryButtonStyle}>
                {t('mobile.recording.pause', { defaultValue: 'Pausar' })}
              </button>
              <button
                type="button"
                onClick={stopRecording}
                style={{ ...secondaryButtonStyle, color: '#DC2626', borderColor: '#DC2626' }}
              >
                {t('mobile.recording.stop', { defaultValue: 'Parar' })}
              </button>
            </div>
          </div>
        )}

        {micState === 'paused' && (
          <div style={blockStyle}>
            <h2 style={titleAmberStyle}>
              {t('mobile.recording.paused_title', { defaultValue: 'Gravação pausada' })}
            </h2>
            <div style={timerStyle}>{formatTimer(recordingSeconds)}</div>
            <p style={hintStyle}>
              {t('mobile.recording.paused_hint', {
                defaultValue: 'Áudio guardado. Retome quando estiver pronto.',
              })}
            </p>
            <div style={secondaryButtonRowStyle}>
              <button
                type="button"
                onClick={resumeRecording}
                style={{ ...secondaryButtonStyle, color: 'var(--green-fluor)', borderColor: 'var(--green-fluor)' }}
              >
                {t('mobile.recording.resume', { defaultValue: 'Retomar' })}
              </button>
              <button
                type="button"
                onClick={stopRecording}
                style={{ ...secondaryButtonStyle, color: '#DC2626', borderColor: '#DC2626' }}
              >
                {t('mobile.recording.stop', { defaultValue: 'Parar' })}
              </button>
            </div>
          </div>
        )}

        {micState === 'uploading' && (
          <StatusBlock
            text={t('mic.uploading')}
            spinner
            tone="violet"
          />
        )}

        {micState === 'uploaded' && (
          <div style={blockStyle}>
            <div style={uploadedHeroStyle}>{t('mic.uploaded')}</div>
            <p style={hintStyle}>{t('mic.uploaded_hint')}</p>
          </div>
        )}

        {micState === 'error' && (
          <div style={blockStyle}>
            <h2 style={titleCoralStyle}>
              {t('mic.error_title')}: {errorMessage ?? ''}
            </h2>
            <p style={hintStyle}>{t('mic.error_retry')}</p>
          </div>
        )}
      </main>

      {/* Rodapé — sessão + queue + hints + WakeLockBadge + emergência */}
      <footer style={footerStyle}>
        {/* Countdown sessão 12h (#6) — só mostra quando há expiresAt válido */}
        {expiresAt !== null && sessionRemainingMs !== null && sessionRemainingMs > 0 && (
          <div style={sessionBadgeStyle}>
            <span style={{ color: 'var(--violet)' }}>●</span>{' '}
            {t('mobile.session.connected', { defaultValue: 'Conectado' })} ·{' '}
            {formatRemaining(sessionRemainingMs)}
          </div>
        )}

        {/* WakeLockBadge (#12) — só renderiza se active OU failed */}
        <WakeLockBadge active={wakeLockActive} failed={wakeLockFailed} t={t} />

        {showIosWakeHint && (
          <div style={iosHintStyle}>{t('mic.ios_wake_hint')}</div>
        )}

        {queue.length > 0 && (
          <div style={queueStyle}>
            <div style={queueLabelStyle}>{t('mic.queued_label')}</div>
            <ul style={queueListStyle}>
              {queue.map((q) => (
                <li key={q.id} style={queueItemStyle}>
                  ✓ {formatTimer(q.seconds)} ·{' '}
                  {new Date(q.sentAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Botão emergência "encerrar sessão" (#13) — só visível quando
            a sessão está realmente ativa (faz pouco sentido em validating
            ou invalid_token, e estética coral já indica "perigo"). */}
        {micState !== 'validating' && micState !== 'invalid_token' && (
          <button
            type="button"
            onClick={endSession}
            style={endSessionButtonStyle}
            aria-label={t('mobile.end_session.button', { defaultValue: 'Encerrar sessão' })}
          >
            {t('mobile.end_session.button', { defaultValue: 'Encerrar sessão' })}
          </button>
        )}
      </footer>
    </div>
  );
};

export default MobileMicPage;
export { MobileMicPage };

/* ------------------------------------------------------------------------- */
/* Subcomponentes internos                                                     */
/* ------------------------------------------------------------------------- */

interface StatusBlockProps {
  readonly text: string;
  readonly spinner?: boolean;
  readonly tone?: 'coral' | 'green' | 'violet' | 'mute';
}

const StatusBlock: FC<StatusBlockProps> = ({ text, spinner, tone = 'mute' }) => {
  const color =
    tone === 'coral'
      ? '#DC2626'
      : tone === 'green'
      ? 'var(--green-fluor)'
      : tone === 'violet'
      ? 'var(--violet)'
      : 'var(--ink-light)';
  return (
    <div style={blockStyle}>
      {spinner && (
        <div style={spinnerStyle} aria-hidden="true">
          <span style={{ animation: 'cursor-blink 0.6s step-end infinite' }}>●</span>
          <span style={{ animation: 'cursor-blink 0.6s step-end 0.2s infinite' }}>●</span>
          <span style={{ animation: 'cursor-blink 0.6s step-end 0.4s infinite' }}>●</span>
        </div>
      )}
      <p style={{ ...statusTextStyle, color }}>{text}</p>
    </div>
  );
};

interface BigButtonProps {
  readonly variant: 'record' | 'stop';
  readonly label: string;
  readonly hint: string;
  readonly onClick: () => void;
  readonly timer?: string;
}

const BigButton: FC<BigButtonProps> = ({ variant, label, hint, onClick, timer }) => {
  const isRecord = variant === 'record';
  // Record = resting CTA → cta-blue filled; Stop = live recording ACTIVE → green allowed
  const accent = isRecord ? 'var(--cta-blue)' : 'var(--green-fluor)';
  const glow = isRecord ? 'none' : 'var(--green-glow-md)';

  return (
    <div style={blockStyle}>
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        style={{
          width: 220,
          height: 220,
          borderRadius: '50%',
          border: `2px solid ${accent}`,
          background: isRecord ? 'var(--cta-blue)' : 'var(--ink-soft)',
          color: isRecord ? '#FFFFFF' : accent,
          fontFamily: 'var(--font-sans)',
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: '0.03em',
          textAlign: 'center',
          cursor: 'pointer',
          boxShadow: glow,
          textShadow: glow,
          padding: 12,
          // Pulso suave no botão de gravar (chama atenção); waveform-like no parar
          animation: isRecord ? 'mic-pulse 1.4s ease-in-out infinite' : 'mic-throb 0.8s ease-in-out infinite',
        }}
      >
        {label}
      </button>
      {timer !== undefined && (
        <div style={timerStyle}>{timer}</div>
      )}
      <p style={hintStyle}>{hint}</p>
    </div>
  );
};

/* ------------------------------------------------------------------------- */
/* Estilos inline (CSS-in-JS via objetos — sem dependência de CSS modules)    */
/* ------------------------------------------------------------------------- */

const pageStyle: CSSProperties = {
  minHeight: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--ink)',
  color: 'var(--ink-light)',
  fontFamily: 'var(--font-sans)',
  padding: '16px 16px 24px',
  boxSizing: 'border-box',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
  marginBottom: 12,
  flexShrink: 0,
  position: 'relative', // âncora pro BatteryIndicator absoluto
};

const bannerStyle: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  lineHeight: 1.15,
  color: 'var(--ink-light)',
  textShadow: 'none',
  letterSpacing: '0.02em',
};

const statusRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

const tierBadgeStyle: CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 12,
  color: 'var(--ink-mute)',
  textShadow: 'none',
  border: '1px solid rgba(124, 124, 248, 0.30)',
  padding: '3px 8px',
  letterSpacing: '0.08em',
};

const langSwitcherStyle: CSSProperties = {
  display: 'flex',
  gap: 4,
  marginTop: 4,
};

const langButtonStyle: CSSProperties = {
  // 32×32 — abaixo do recomendado WCAG (44px) mas próximo o suficiente
  // pra header secundário; touch target real fica ~36px com padding.
  minWidth: 32,
  minHeight: 32,
  padding: '6px 8px',
  background: 'transparent',
  border: '1px solid transparent',
  fontFamily: 'var(--font-sans)',
  fontSize: 12,
  letterSpacing: '0.08em',
  cursor: 'pointer',
  borderRadius: 0,
  transition: 'color 120ms ease-out, border-color 120ms ease-out',
};

const mainStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 16,
  textAlign: 'center',
};

const blockStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 14,
  maxWidth: 360,
};

const statusTextStyle: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-sans)',
  fontSize: 22,
  lineHeight: 1.3,
};

const titleCoralStyle: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  color: '#DC2626',
  textShadow: 'none',
  letterSpacing: '0.05em',
  lineHeight: 1.4,
};

const titleAmberStyle: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  color: '#FFC107',
  letterSpacing: '0.05em',
  lineHeight: 1.4,
};

const hintStyle: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  color: 'var(--ink-mute)',
  lineHeight: 1.45,
};

const smallButtonStyle: CSSProperties = {
  padding: '10px 18px',
  background: 'var(--cta-blue)',
  color: '#FFFFFF',
  border: '1px solid var(--cta-blue)',
  fontFamily: 'var(--font-sans)',
  fontSize: 12,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  borderRadius: 0,
  boxShadow: 'none',
};

/** Botões secundários do estado recording/paused (#8) — touch-friendly 44px. */
const secondaryButtonRowStyle: CSSProperties = {
  display: 'flex',
  gap: 12,
  marginTop: 8,
};

const secondaryButtonStyle: CSSProperties = {
  minWidth: 110,
  minHeight: 44,
  padding: '10px 18px',
  background: 'transparent',
  color: 'var(--ink-light)',
  border: '1px solid var(--ink-mute)',
  fontFamily: 'var(--font-sans)',
  fontSize: 12,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  borderRadius: 0,
};

const spinnerStyle: CSSProperties = {
  display: 'flex',
  gap: 6,
  color: 'var(--violet)',
  fontSize: 24,
  textShadow: 'none',
};

const timerStyle: CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
  color: 'var(--violet)',
  textShadow: 'none',
  letterSpacing: '0.1em',
  marginTop: 4,
};

const uploadedHeroStyle: CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 22,
  color: '#FFFFFF',
  textShadow: '0 0 12px rgba(124, 124, 248, 0.55)',
  letterSpacing: '0.08em',
  // Fade-in suave pra reforçar "✓ enviado" (#10).
  animation: 'cursor-blink 0.4s step-end 1',
};

const footerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
  flexShrink: 0,
  fontSize: 12,
  color: 'var(--ink-mute)',
};

const sessionBadgeStyle: CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 12,
  color: 'var(--ink-light)',
  letterSpacing: '0.02em',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};

const iosHintStyle: CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 12,
  color: 'var(--ink-mute)',
  opacity: 0.85,
  textAlign: 'center',
  maxWidth: 320,
};

const queueStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
  marginTop: 8,
  width: '100%',
  maxWidth: 320,
};

const queueLabelStyle: CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 12,
  color: 'var(--ink-mute)',
  textShadow: 'none',
  letterSpacing: '0.1em',
};

const queueListStyle: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  alignItems: 'center',
  fontFamily: 'var(--font-sans)',
  fontSize: 12,
  color: 'var(--ink-light)',
};

const queueItemStyle: CSSProperties = {
  letterSpacing: '0.02em',
};

/**
 * Botão emergência "encerrar sessão" (#13) — discreto mas vermelho
 * o suficiente pra ser claramente "perigo". 44px touch-friendly.
 */
const endSessionButtonStyle: CSSProperties = {
  marginTop: 16,
  minHeight: 44,
  padding: '10px 20px',
  background: 'transparent',
  color: '#DC2626',
  border: '1px solid #DC2626',
  fontFamily: 'var(--font-sans)',
  fontSize: 12,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  borderRadius: 0,
  opacity: 0.75,
};
